import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Shield, Plus, Trash2, Edit, Check, X, User, Key
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PERMISSIONS = ['users', 'groups', 'finance', 'settings', 'logs', 'admins'];

const AdminManagement = () => {
  const { token, admin } = useOutletContext();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [createForm, setCreateForm] = useState({
    username: '',
    password: '',
    name: '',
    role: 'admin',
    permissions: []
  });
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchAdmins();
  }, [token]);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/admins?token=${token}`);
      setAdmins(response.data.admins);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Super admin access required');
      } else {
        toast.error('Failed to fetch admins');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!createForm.username || !createForm.password || !createForm.name) {
      toast.error('Please fill all required fields');
      return;
    }

    try {
      const params = new URLSearchParams({
        token,
        username: createForm.username,
        password: createForm.password,
        name: createForm.name,
        role: createForm.role
      });
      createForm.permissions.forEach(p => params.append('permissions', p));
      
      await axios.post(`${API}/admin/admins?${params}`);
      toast.success('Admin created successfully');
      setShowCreate(false);
      setCreateForm({ username: '', password: '', name: '', role: 'admin', permissions: [] });
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create admin');
    }
  };

  const handleEdit = (adm) => {
    setSelectedAdmin(adm);
    setEditForm({
      is_active: adm.is_active,
      permissions: adm.permissions || []
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    try {
      const params = new URLSearchParams({
        token,
        is_active: editForm.is_active
      });
      editForm.permissions.forEach(p => params.append('permissions', p));
      
      await axios.put(`${API}/admin/admins/${selectedAdmin.id}?${params}`);
      toast.success('Admin updated successfully');
      setShowEdit(false);
      fetchAdmins();
    } catch (error) {
      toast.error('Failed to update admin');
    }
  };

  const handleDelete = async (adminId) => {
    if (!confirm('Are you sure you want to delete this admin?')) return;
    try {
      await axios.delete(`${API}/admin/admins/${adminId}?token=${token}`);
      toast.success('Admin deleted');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete admin');
    }
  };

  const togglePermission = (permission, form, setForm) => {
    const current = form.permissions || [];
    if (current.includes(permission)) {
      setForm({ ...form, permissions: current.filter(p => p !== permission) });
    } else {
      setForm({ ...form, permissions: [...current, permission] });
    }
  };

  if (admin?.role !== 'super_admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
          <p className="text-slate-400">Super admin access required</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-white">Admin Users</h2>
          <p className="text-slate-400 text-sm">Manage administrator accounts</p>
        </div>
        <Button
          onClick={() => setShowCreate(true)}
          className="bg-gradient-to-r from-red-500 to-orange-500"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Admin
        </Button>
      </div>

      {/* Admins Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : admins.length === 0 ? (
          <div className="col-span-full text-center py-12 text-slate-400">
            No admin users found
          </div>
        ) : (
          admins.map((adm, index) => (
            <motion.div
              key={adm.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-slate-900 rounded-2xl border border-slate-800 p-5"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    adm.role === 'super_admin' 
                      ? 'bg-gradient-to-br from-red-500 to-orange-500'
                      : 'bg-slate-700'
                  }`}>
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">{adm.name}</h3>
                    <p className="text-sm text-slate-400">@{adm.username}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  adm.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                }`}>
                  {adm.is_active ? 'Active' : 'Disabled'}
                </span>
              </div>

              <div className="mb-4">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  adm.role === 'super_admin' 
                    ? 'bg-red-500/20 text-red-400'
                    : 'bg-blue-500/20 text-blue-400'
                }`}>
                  {adm.role?.replace('_', ' ')}
                </span>
              </div>

              {adm.permissions && adm.permissions.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-4">
                  {adm.permissions.map(perm => (
                    <span key={perm} className="px-2 py-0.5 rounded text-xs bg-slate-800 text-slate-400 capitalize">
                      {perm}
                    </span>
                  ))}
                </div>
              )}

              {adm.last_login && (
                <p className="text-xs text-slate-500 mb-4">
                  Last login: {new Date(adm.last_login).toLocaleDateString()}
                </p>
              )}

              {adm.id !== admin.id && adm.role !== 'super_admin' && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(adm)}
                    className="flex-1 border-slate-700"
                  >
                    <Edit className="w-4 h-4 mr-2" /> Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(adm.id)}
                    className="border-slate-700 text-red-400 hover:text-red-300"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </motion.div>
          ))
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Create Admin User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-slate-400">Username *</label>
                <Input
                  value={createForm.username}
                  onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                  className="mt-1 bg-slate-800 border-slate-700 text-white rounded-xl"
                  placeholder="admin_user"
                />
              </div>
              <div>
                <label className="text-sm text-slate-400">Password *</label>
                <Input
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  className="mt-1 bg-slate-800 border-slate-700 text-white rounded-xl"
                  placeholder="••••••••"
                />
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-400">Full Name *</label>
              <Input
                value={createForm.name}
                onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                className="mt-1 bg-slate-800 border-slate-700 text-white rounded-xl"
                placeholder="John Doe"
              />
            </div>
            <div>
              <label className="text-sm text-slate-400">Role</label>
              <select
                value={createForm.role}
                onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                className="w-full mt-1 px-4 py-2 bg-slate-800 border border-slate-700 text-white rounded-xl"
              >
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            {createForm.role === 'admin' && (
              <div>
                <label className="text-sm text-slate-400 mb-2 block">Permissions</label>
                <div className="flex flex-wrap gap-2">
                  {PERMISSIONS.map(perm => (
                    <button
                      key={perm}
                      onClick={() => togglePermission(perm, createForm, setCreateForm)}
                      className={`px-3 py-1.5 rounded-full text-sm capitalize transition-all ${
                        createForm.permissions?.includes(perm)
                          ? 'bg-orange-500 text-white'
                          : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                      }`}
                    >
                      {perm}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="flex gap-3 pt-4">
              <Button onClick={() => setShowCreate(false)} variant="outline" className="flex-1 border-slate-700">
                Cancel
              </Button>
              <Button onClick={handleCreate} className="flex-1 bg-gradient-to-r from-red-500 to-orange-500">
                Create Admin
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white">
          <DialogHeader>
            <DialogTitle>Edit Admin: {selectedAdmin?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-xl bg-slate-800/50">
              <span className="text-slate-300">Account Active</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditForm({ ...editForm, is_active: true })}
                  className={`p-2 rounded-lg ${
                    editForm.is_active ? 'bg-green-500/20 text-green-400' : 'bg-slate-700 text-slate-400'
                  }`}
                >
                  <Check className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setEditForm({ ...editForm, is_active: false })}
                  className={`p-2 rounded-lg ${
                    !editForm.is_active ? 'bg-red-500/20 text-red-400' : 'bg-slate-700 text-slate-400'
                  }`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div>
              <label className="text-sm text-slate-400 mb-2 block">Permissions</label>
              <div className="flex flex-wrap gap-2">
                {PERMISSIONS.map(perm => (
                  <button
                    key={perm}
                    onClick={() => togglePermission(perm, editForm, setEditForm)}
                    className={`px-3 py-1.5 rounded-full text-sm capitalize transition-all ${
                      editForm.permissions?.includes(perm)
                        ? 'bg-orange-500 text-white'
                        : 'bg-slate-800 text-slate-400 hover:bg-slate-700'
                    }`}
                  >
                    {perm}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={() => setShowEdit(false)} variant="outline" className="flex-1 border-slate-700">
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} className="flex-1 bg-gradient-to-r from-red-500 to-orange-500">
                Save Changes
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminManagement;
